import {
  fetchLatestRelationalIntroductionLetter,
  generateRelationalIntroductionLetter,
  markRelationalIntroductionLetterDone,
  regenerateRelationalIntroductionLetter,
  saveRelationalIntroductionLetter,
} from '@/api/scansAPI'
import UpgradeCTA from '@/components/UpgradeCTA'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import { ScanResponse } from '@/models/Scans'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LaunchIcon from '@mui/icons-material/Launch'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import React from 'react'
import { useEffect, useState } from 'react'
import { useProfileRegenGuard } from '@/components/ProfileRegenBlockProvider'

interface GenerateRelationalIntroductionLetterProps {
  isOpen: boolean
  onClose: () => void
  token: string
  result: ScanResponse
  onDraftStateChange?: (state: 'none' | 'generating' | 'draft' | 'done') => void
}

const modalSx = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '94vw', sm: '760px' },
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 3,
  p: { xs: 2.5, sm: 4 },
}

function renderTextWithLinks(text: string) {
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g
  const parts = text.split(urlRegex)

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      const href = part.startsWith('www.') ? `https://${part}` : part
      return (
        <React.Fragment key={index}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {part}
          </a>
        </React.Fragment>
      )
    }
    return <React.Fragment key={index}>{part}</React.Fragment>
  })
}

export default function GenerateRelationalIntroductionLetter({
  isOpen,
  onClose,
  token,
  result,
  onDraftStateChange,
}: GenerateRelationalIntroductionLetterProps) {
  const { canWrite, isLoading: orgLoading } = usePausedOrg()
  const guardRegen = useProfileRegenGuard()
  const [generatedLetter, setGeneratedLetter] = useState('')
  const [savedLetter, setSavedLetter] = useState('')
  const [contactPathwayContext, setContactPathwayContext] = useState('')
  const [memoriesApplied, setMemoriesApplied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasLoadedDraft, setHasLoadedDraft] = useState(false)
  const [showCopySuccess, setShowCopySuccess] = useState(false)
  const [draftVersion, setDraftVersion] = useState<number | null>(null)
  const [isDone, setIsDone] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showRegenDialog, setShowRegenDialog] = useState(false)
  const [regenerationFeedback, setRegenerationFeedback] = useState('')
  const [isUsageLimitError, setIsUsageLimitError] = useState(false)

  const resetState = () => {
    setGeneratedLetter('')
    setSavedLetter('')
    setContactPathwayContext('')
    setMemoriesApplied(false)
    setError(null)
    setIsSubmitting(false)
    setHasLoadedDraft(false)
    setDraftVersion(null)
    setIsSaving(false)
    setShowRegenDialog(false)
    setRegenerationFeedback('')
    setIsDone(false)
    setIsUsageLimitError(false)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const applyDraftResponse = (response: {
    draft: {
      content: string
      contact_pathway_context?: string
      memories_applied?: boolean
      version_number: number
      is_done: boolean
    }
  }) => {
    setGeneratedLetter(response.draft.content)
    setSavedLetter(response.draft.content)
    setContactPathwayContext(response.draft.contact_pathway_context || '')
    setMemoriesApplied(Boolean(response.draft.memories_applied))
    setDraftVersion(response.draft.version_number)
    setIsDone(response.draft.is_done)
    setHasLoadedDraft(true)
    onDraftStateChange?.(response.draft.is_done ? 'done' : 'draft')
  }

  const hasUnsavedChanges = generatedLetter !== savedLetter

  const handleGenerate = async () => {
    if (!result.id) {
      setError('Missing scan result ID')
      return
    }

    setIsSubmitting(true)
    setError(null)
    // Surface the in-flight state to the trigger button so it holds a
    // "Drafting..." state that persists even if this modal is closed.
    onDraftStateChange?.('generating')

    try {
      const response = await generateRelationalIntroductionLetter(
        token,
        result.id,
      )
      applyDraftResponse(response)
    } catch (err) {
      if (guardRegen(err)) {
        setIsSubmitting(false)
        return
      }
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to generate relational introduction letter'
      const limitHit = message.includes('Free trial limit reached')
      setIsUsageLimitError(limitHit)
      setError(message)
      setHasLoadedDraft(true)
      // clear generating state
      onDraftStateChange?.('none')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSave = async () => {
    if (!result.id || !generatedLetter.trim()) return
    setIsSaving(true)
    setError(null)
    try {
      const response = await saveRelationalIntroductionLetter(
        token,
        result.id,
        generatedLetter,
      )
      applyDraftResponse(response)
    } catch {
      setError('Failed to save introduction letter')
    } finally {
      setIsSaving(false)
    }
  }

  const handleRegenerate = async () => {
    if (!result.id || !regenerationFeedback.trim()) return
    setIsSubmitting(true)
    setError(null)
    try {
      const response = await regenerateRelationalIntroductionLetter(
        token,
        result.id,
        regenerationFeedback,
      )
      applyDraftResponse(response)
      setShowRegenDialog(false)
      setRegenerationFeedback('')
    } catch (err) {
      if (guardRegen(err)) return
      setError('Failed to regenerate introduction letter')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleMarkDone = async () => {
    if (!result.id || !generatedLetter.trim()) return
    setIsSaving(true)
    setError(null)
    try {
      const response = await markRelationalIntroductionLetterDone(
        token,
        result.id,
        generatedLetter,
      )
      applyDraftResponse(response)
    } catch {
      setError('Failed to mark introduction letter as done')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedLetter)
      setShowCopySuccess(true)
    } catch {
      setError('Failed to copy introduction letter')
    }
  }

  useEffect(() => {
    const load = async () => {
      if (!isOpen || hasLoadedDraft || isSubmitting) return
      if (!result.id) return
      setIsSubmitting(true)
      setError(null)
      try {
        const latest = await fetchLatestRelationalIntroductionLetter(
          token,
          result.id,
        )
        if (latest) {
          applyDraftResponse(latest)
        } else {
          await handleGenerate()
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load relational introduction letter',
        )
      } finally {
        setIsSubmitting(false)
      }
    }
    load()
  }, [hasLoadedDraft, isOpen, isSubmitting])

  return (
    <>
      <Modal open={isOpen} onClose={handleClose}>
        <Box sx={modalSx}>
          <IconButton
            onClick={handleClose}
            aria-label="Close introduction letter modal"
            sx={{
              position: 'absolute',
              top: { xs: 12, sm: 16 },
              right: { xs: 12, sm: 16 },
              color: '#1C3045',
            }}
          >
            <CloseIcon sx={{ fontSize: 32 }} />
          </IconButton>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="h5"
                sx={{ color: '#1C3045', fontWeight: 700 }}
              >
                Draft introduction letter
              </Typography>
              <Typography variant="body2" sx={{ color: '#5A6B7B', mt: 1 }}>
                We draft the first version automatically using the relational
                match, organisation context, and any saved memories. You can
                then save edits or regenerate it with feedback.
              </Typography>
            </Box>

            <Box
              sx={{
                bgcolor: '#F4F8FB',
                borderRadius: 2,
                px: 2,
                py: 1.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: '#1C3045', mb: 0.5 }}
              >
                Match context
              </Typography>
              <Typography variant="body2" sx={{ color: '#1C3045' }}>
                {result.funder.name}
                {result.opportunity.fund_name
                  ? ` · ${result.opportunity.fund_name}`
                  : ''}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5A6B7B', mt: 1 }}>
                {result.result}
              </Typography>
            </Box>

            {memoriesApplied && (
              <Alert severity="info">
                This draft included relevant organisational memories.
              </Alert>
            )}

            {contactPathwayContext && (
              <Alert severity="info">
                {renderTextWithLinks(contactPathwayContext)}
              </Alert>
            )}

            {!orgLoading && !canWrite && (
              <Alert severity="warning">
                Your account is in read-only mode. Generating, saving,
                regenerating, and marking as done are disabled.
              </Alert>
            )}

            {error && canWrite && <Alert severity="error">{error}</Alert>}

            {isUsageLimitError && (
              <UpgradeCTA
                variant="inline"
                restrictedFeature="Drafting more introduction letters"
                message="Upgrade to a paid plan to generate more introduction letters."
              />
            )}

            {isSubmitting && (
              <Alert
                severity="info"
                icon={<CircularProgress size={18} color="inherit" />}
              >
                Drafting your introduction letter...
              </Alert>
            )}

            {!isSubmitting && !isUsageLimitError && (
              <Stack spacing={1.5}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#1C3045', fontWeight: 700 }}
                >
                  Draft
                </Typography>
                <TextField
                  value={generatedLetter}
                  onChange={(event) => setGeneratedLetter(event.target.value)}
                  multiline
                  minRows={12}
                />
              </Stack>
            )}

            {!isUsageLimitError && (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={
                    (!orgLoading && !canWrite) ||
                    isSaving ||
                    isSubmitting ||
                    !generatedLetter ||
                    !hasUnsavedChanges
                  }
                  sx={{
                    textTransform: 'none',
                    bgcolor: '#1C3045',
                    borderRadius: 2,
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save draft'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowRegenDialog(true)}
                  disabled={(!orgLoading && !canWrite) || isSubmitting}
                  startIcon={<AutoAwesomeIcon />}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  Regenerate draft
                </Button>
                <Button
                  variant="contained"
                  onClick={handleMarkDone}
                  disabled={
                    (!orgLoading && !canWrite) ||
                    isSaving ||
                    isSubmitting ||
                    !generatedLetter ||
                    isDone
                  }
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    bgcolor: '#2E7D32',
                    '&:hover': {
                      bgcolor: '#256529',
                    },
                  }}
                >
                  {isDone ? 'Done' : 'Mark as done'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCopy}
                  disabled={!generatedLetter}
                  startIcon={<ContentCopyIcon />}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Copy letter
                </Button>
                {result.funder.website && (
                  <Button
                    component="a"
                    href={result.funder.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="text"
                    startIcon={<LaunchIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    Open funder website
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        </Box>
      </Modal>

      <Snackbar
        open={showCopySuccess}
        autoHideDuration={2500}
        onClose={() => setShowCopySuccess(false)}
        message="Introduction letter copied"
      />
      <Dialog
        open={showRegenDialog}
        onClose={() => setShowRegenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Regenerate with feedback</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            margin="dense"
            label="What should change in the next draft?"
            value={regenerationFeedback}
            onChange={(event) => setRegenerationFeedback(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRegenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRegenerate}
            disabled={isSubmitting || !regenerationFeedback.trim()}
          >
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
